import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconTrash } from "@tabler/icons-react";
import { reloadCurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import {
  openDesktopSettings,
  leaveDesktopSettingsPage,
} from "../../../../domains/app/stores/desktopSettingsStore";
import {
  apiClient,
  revalidateApiQuery,
  useApiQuery,
} from "../../../../services/api";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../../../domains/room/utils/getRoomLogoUrl";
import { displayToast } from "../../../../components/app/AppToast";
import { Avatar } from "../../../../components/ui/Avatar";
import { Button } from "../../../../components/ui/Button";
import Loader from "../../../../components/Loader";
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
} from "../components/SettingsPanel";

const NOT_LEAVING = { page: "rooms", leaving: null } as const;

/**
 * My rooms (Figma "D3.settings.rooms" and "…rooms.leave"): the rooms this
 * account has joined, each with a trash that asks in the row itself before
 * leaving. `leaving` is the room whose row asks; a `/settings/rooms/leave/:id`
 * address opens on it.
 */
export function RoomsSection({
  leaving,
}: {
  leaving: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const roomsQuery = useApiQuery("/webapp/private/rooms", {});

  return (
    <SettingsPage back={null}>
      <SettingsSection title={t("My rooms")} description={null}>
        {roomsQuery.state === "pending" ? (
          <Loader expanded />
        ) : roomsQuery.state === "error" ? (
          <p className="text-sm text-ds-slate-700">{roomsQuery.message}</p>
        ) : roomsQuery.body.rooms.length === 0 ? (
          <p className="py-2.5 text-sm text-ds-slate-700">
            {t("You haven't joined any rooms yet.")}
          </p>
        ) : (
          roomsQuery.body.rooms.map((room) => {
            const roomName = getRoomName(room, activeLang);
            const row = (
              <SettingsRow
                leading={
                  <Avatar
                    variant="image"
                    size="md"
                    imageFit="scale-down"
                    src={getRoomLogoUrl(room)}
                    alt={roomName}
                    badge={null}
                  />
                }
                title={roomName}
                subtitle={t("by {{name}}", {
                  name: room.organisationModel.name,
                })}
                right={
                  room.id === leaving ? null : (
                    <button
                      type="button"
                      aria-label={t("Leave Room")}
                      onClick={() => {
                        openDesktopSettings({
                          page: "rooms",
                          leaving: room.id,
                        });
                      }}
                      className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-ds-strawberry-700 hover:bg-ds-strawberry-50 [&_svg]:size-4"
                    >
                      <IconTrash stroke={1.5} />
                    </button>
                  )
                }
              />
            );
            return room.id === leaving ? (
              <LeaveConfirmation key={room.id} roomId={room.id}>
                {row}
              </LeaveConfirmation>
            ) : (
              <div key={room.id}>{row}</div>
            );
          })
        )}
      </SettingsSection>
    </SettingsPage>
  );
}

/** The row of the room being left, asking in place (Figma "…rooms.leave"). */
function LeaveConfirmation({
  roomId,
  children,
}: {
  roomId: string;
  /** The room's own row, kept on top of the question. */
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  // useForm only for a submit-in-flight flag, without owning `useState`.
  const { handleSubmit, formState } = useForm();

  async function onLeave(): Promise<void> {
    try {
      await apiClient.delete("/webapp/private/rooms/:id/leave", {
        param: { id: roomId },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    leaveDesktopSettingsPage({ page: "rooms", leaving: roomId }, NOT_LEAVING);
    displayToast(t("You've left the space"), "success");
    await revalidateApiQuery("/webapp/private/rooms");
    void revalidateApiQuery("/webapp/private/playlists/recommended");
    await reloadCurrentUser();
  }

  return (
    <div className="flex flex-col gap-1 rounded-xl bg-ds-slate-100 px-3 pb-3">
      {children}
      <form
        className="flex items-center gap-3"
        onSubmit={handleSubmit(onLeave)}
      >
        <p className="m-0 min-w-0 flex-1 text-sm text-ds-slate-950">
          {t("Leave this room? You can follow it again from Rooms.")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-w-37 shrink-0"
          onClick={() => {
            openDesktopSettings(NOT_LEAVING);
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          size="sm"
          className="min-w-37 shrink-0"
          disabled={formState.isSubmitting}
        >
          {t("Leave")}
        </Button>
      </form>
    </div>
  );
}
